import { TIME_RANGES } from 'core/components/widgets/TimePicklist'
import TrendGraph from 'core/components/widgets/TrendGraph'
import useTrendData from 'core/components/widgets/useTrendData'
import React, { useEffect, useMemo, useState } from 'react'

interface Props {
  title: string
  units: string
  metricKey?: string
  resourceId?: string
  stats?: { current: number; max: number; percent: number }
  precision?: number
  resources?: any[]
}

export default function TrendGraphWidget({
  title,
  units,
  metricKey,
  resourceId,
  stats,
  precision = 1,
  resources = [],
}: Props) {
  const [currentTimeRange, setCurrentTimeRange] = useState(TIME_RANGES.last_1_hour)
  const { current, max } = stats || {}

  const showFooterStats = current !== undefined && max !== undefined
  const curStr = current?.toFixed(precision)
  const maxStr = max?.toFixed(precision)
  const availableStr = showFooterStats ? (max - current).toFixed(precision) : undefined

  const { values, average, reload } = useTrendData({
    metricKey,
    currentTimeRange,
    params: {
      id: resourceId,
    },
    resources,
  })

  // Add event listener for refreshGraph event
  useEffect(() => {
    document.addEventListener('refreshGraph', reload)

    return () => {
      document.removeEventListener('refreshGraph', reload)
    }
  }, [reload])

  // useMemo to prevent re-rendering of graph when resources change
  const graphData = useMemo(() => {
    return {
      title,
      averageValue: average,
      ...(showFooterStats && {
        footerValues: [
          { key: 'PROVISIONED', value: curStr },
          { key: 'AVAILABLE', value: availableStr },
          { key: 'CAPACITY', value: maxStr },
        ],
      }),
      unit: units,
      graph: {
        values,
        keys: [
          {
            name: 'value',
            color: '#0089C7',
            legendName: 'Usage',
            offsetFirst: '0%',
            offsetSecond: '113.39%',
            stopOpacityFirst: 0.3,
            stopOpacitySecond: 0.1,
          },
        ],
      },
    }
  }, [values, average, showFooterStats, curStr, availableStr, maxStr, title, units])

  return (
    <TrendGraph
      data={graphData}
      onTimeRangeChange={setCurrentTimeRange}
      currentTimeRange={currentTimeRange}
    />
  )
}
